import React, { useState, useEffect, useRef, startTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { COUPONS } from '../../common/constants/coupons';

interface SignOnFeeFeature {
  title: string;
  description: string;
}

interface SignOnFeeProps {
  discount_code?: string;
}

const SignOnFeeSection: React.FC<SignOnFeeProps> = ({ discount_code }) => {
  const { t } = useTranslation();
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const scrollRef = useRef<HTMLUListElement>(null);

  const features = t('signOnFee.features', { returnObjects: true }) as SignOnFeeFeature[];

  const getPrice = (discount_code?: string) => {
    if (COUPONS.SIGNON_FEE_COUPONS.includes(discount_code)) {
      if (COUPONS.OFF_50_COUPONS.includes(discount_code)) {
        return 'A$1750';
      } else if (COUPONS.SIGNON_FEE_COUPONS_OFF_50_WITH_TAX.includes(discount_code)) {
        return 'A$1925';
      } else if (COUPONS.OFF_100_COUPONS.includes(discount_code)) {
        return 'A$0';
      } else {
        return 'A$875';
      }
    } else return t('signOnFee.price');
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (scrollRef.current) {
        const { scrollHeight, clientHeight } = scrollRef.current;
        startTransition(() => setHasOverflow(scrollHeight > clientHeight));
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [features]);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 10);
    }
  };

  return (
    <section className='signOnFeeSection'>
      <div className='signOnFeeCard'>
        <div className='signOnFeeHeader'>
          <div className='signOnFeeContent'>
            <div className='signOnFeeTitleRow'>
              <h2 className='signOnFeeTitle'>{t('signOnFee.header')}</h2>
              <div className='signOnFeeTooltipContainer'>
                {/* <svg fill='currentColor' viewBox='0 0 20 20'>
                  <path
                    fillRule='evenodd'
                    d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                    clipRule='evenodd'
                  />
                </svg>
                {showTooltip && (
                  <div className='signOnFeeTooltip tooltip tooltipVisible'>
                    {t('signOnFee.tooltip')}
                  </div>
                )} */}
              </div>
            </div>
            <p className='signOnFeeDescription'>{t('signOnFee.description')}</p>
          </div>
          <div className='signOnFeePriceSection'>
            <div className='signOnFeePriceWrapper'>
              <span className='signOnFeePrice'>{getPrice(discount_code)}</span>
              <span className='signOnFeeGst'>
                {discount_code === 'ESSA35'
                  ? ' (' + t('planPageMiscellaneous.includingGST') + ')'
                  : t('signOnFee.gst')}
              </span>
              <p className='signOnFeePeriod'>{t('signOnFee.period')}</p>
            </div>
          </div>
        </div>
        <div>
          <h3 className='signOnFeeIncludesTitle'>{t('signOnFee.includesTitle')}</h3>
          <div className='signOnFeeFeatureListWrapper'>
            <ul className='signOnFeeFeatureList' ref={scrollRef} onScroll={handleScroll}>
              {features.map((feature, index) => (
                <li key={index} className='signOnFeeFeatureItem'>
                  <span className='featureIcon'>✔</span>
                  <div className='featureContent'>
                    <strong className='featureTitle'>{feature.title}</strong>
                    <span className='featureDescription'>{feature.description}</span>
                  </div>
                </li>
              ))}
            </ul>
            {hasOverflow && !isAtBottom && <div className='scrollIndicator'></div>}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignOnFeeSection;
